<template>
  <div id="app" :class="{'dark': isDarkMode}" class="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 flex flex-col">
    <header class="fixed top-0 left-0 right-0 bg-white dark:bg-gray-800 z-10 shadow-sm">
      <nav class="container mx-auto px-4 py-3">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <router-link to="/" class="flex items-center">
              <img src="@/assets/images/logo.png" alt="Blog Logo" class="h-8 w-auto sm:h-10 mr-2" />
            </router-link>
          </div>
          
          <!-- Mobile menu button -->
          <div class="flex items-center md:hidden">
            <ThemeToggle v-model="isDarkMode" class="mr-2" />
            <button @click="toggleMobileMenu" class="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 focus:outline-none">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
          
          <!-- Desktop navigation -->
          <div class="hidden md:flex md:items-center md:space-x-4">
            <div class="relative">
              <input
                type="text"
                placeholder="Search..."
                class="w-full md:w-auto pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                v-model="searchQuery"
                @input="debouncedSearch"
              />
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400 dark:text-gray-500 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <template v-if="isAuthenticated">
              <router-link to="/dashboard" class="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">Dashboard</router-link>
              <router-link to="/liked" class="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">Liked Posts</router-link>
              <router-link to="/bookmarks" class="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">Bookmarks</router-link>
              <button @click="logout" class="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">Logout</button>
            </template>
            <template v-else>
              <router-link to="/login" class="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">Login</router-link>
              <router-link to="/signup" class="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">Sign Up</router-link>
            </template>
            <ThemeToggle v-model="isDarkMode" class="hidden md:block" />
          </div>
        </div>
        
        <!-- Mobile menu, show/hide based on menu state -->
        <div :class="{'block': mobileMenuOpen, 'hidden': !mobileMenuOpen}" class="md:hidden mt-4 pb-2">
          <div class="flex flex-col space-y-3">
            <div class="relative">
              <input
                type="text"
                placeholder="Search..."
                class="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                v-model="searchQuery"
                @input="debouncedSearch"
              />
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400 dark:text-gray-500 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <template v-if="isAuthenticated">
              <router-link to="/dashboard" class="block py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">Dashboard</router-link>
              <router-link to="/liked" class="block py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">Liked Posts</router-link>
              <router-link to="/bookmarks" class="block py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">Bookmarks</router-link>
              <button @click="logout" class="block w-full text-left py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">Logout</button>
            </template>
            <template v-else>
              <router-link to="/login" class="block py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">Login</router-link>
              <router-link to="/signup" class="block py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100">Sign Up</router-link>
            </template>
          </div>
        </div>
      </nav>
    </header>

    <main class="container mx-auto px-4 pt-24 pb-12 flex-grow">
      <router-view v-if="$route.name"></router-view>
    </main>

    <footer class="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
      <div class="container mx-auto px-4 py-6">
        <div class="flex flex-col sm:flex-row justify-between items-center">
          <p class="text-gray-600 dark:text-gray-400 mb-4 sm:mb-0">
            &copy; {{ new Date().getFullYear() }} Skix Tech Blog. All rights reserved.
          </p>
          <div class="flex flex-wrap justify-center gap-4">
            <a href="https://skix.co.jp/about" class="text-gray-600 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-200">About</a>
            <a href="https://skix.co.jp/contact" class="text-gray-600 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-200">Contact</a>
            <a href="#" class="text-gray-600 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-200">Privacy Policy</a>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { ref, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import debounce from 'lodash/debounce'
import ThemeToggle from '@/components/ThemeToggle.vue'

export default {
  name: 'App',
  components: {
    ThemeToggle
  },
  setup() {
    const router = useRouter()
    const store = useStore()
    const searchQuery = ref('')
    const isDarkMode = ref(localStorage.getItem('darkMode') === 'true')
    const mobileMenuOpen = ref(false)

    const isAuthenticated = computed(() => store.getters.isAuthenticated)

    const performSearch = () => {
      if (searchQuery.value.trim()) {
        router.push({ name: 'Search', query: { q: searchQuery.value } })
      }
    }

    const debouncedSearch = debounce(performSearch, 2000)

    watch(isDarkMode, (newValue) => {
      localStorage.setItem('darkMode', newValue.toString())
    })

    const logout = () => {
      store.dispatch('logout')
      router.push('/')
    }

    const toggleMobileMenu = () => {
      mobileMenuOpen.value = !mobileMenuOpen.value
    }

    return {
      searchQuery,
      debouncedSearch,
      isDarkMode,
      isAuthenticated,
      logout,
      mobileMenuOpen,
      toggleMobileMenu
    }
  }
}
</script>

<style scoped>
body {
  font-family: 'Inter', sans-serif;
}

.container {
  max-width: 1200px;
  width: 100%;
}
</style>
